import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { vendorService, VendorAnalytics as VendorAnalyticsData } from '../services/vendor.service';

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatCurrencyValue(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

function StatList({ title, data, labelFormatter }: { title: string; data: Record<string, number>; labelFormatter?: (key: string) => string }) {
  const entries = Object.entries(data).sort(([, a], [, b]) => b - a);

  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="font-semibold text-secondary-900 dark:text-white mb-4">{title}</h3>
        {entries.length > 0 ? (
          <div className="space-y-3">
            {entries.map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 dark:text-secondary-400">
                  {labelFormatter ? labelFormatter(key) : key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </span>
                <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                  {formatNumber(value)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-secondary-500 dark:text-secondary-400">No data available</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function VendorAnalytics() {
  const [data, setData] = useState<VendorAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const analyticsData = await vendorService.getVendorAnalytics();
      setData(analyticsData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch vendor analytics:', err instanceof Error ? err.message : err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
            Vendor Analytics & Reporting
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-1">
            Overview of vendor, contract, and compliance metrics across your organization
          </p>
        </div>
      </motion.div>

      {loading ? (
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-lg text-secondary-600 dark:text-secondary-400">
                Loading analytics data...
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : error ? (
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Error Loading Analytics
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                {error}
              </p>
              <Button onClick={fetchData} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      ) : data ? (
        <>
          {/* Top-level Stats */}
          <motion.div variants={ITEM_VARIANTS}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <BuildingOffice2Icon className="w-8 h-8 text-primary-500" />
                    <div>
                      <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                        {formatNumber(data.vendors.total)}
                      </div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">
                        Total Vendors
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <DocumentTextIcon className="w-8 h-8 text-success-500" />
                    <div>
                      <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                        {formatNumber(data.contracts.total)}
                      </div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">
                        Total Contracts
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <CurrencyDollarIcon className="w-8 h-8 text-warning-500" />
                    <div>
                      <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                        {formatCurrencyValue(data.contracts.totalValue)}
                      </div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">
                        Total Contract Value
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <ArrowPathIcon className="w-8 h-8 text-primary-500" />
                    <div>
                      <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                        {formatNumber(data.contracts.autoRenewalCount)}
                      </div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">
                        Auto-Renewals
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Vendor Breakdown */}
          <motion.div variants={ITEM_VARIANTS}>
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
              Vendor Breakdown
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatList
                title="By Status"
                data={data.vendors.byStatus}
                labelFormatter={(key) => vendorService.getVendorStatusConfig(key).label}
              />
              <StatList
                title="By Type"
                data={data.vendors.byType}
                labelFormatter={(key) => vendorService.getVendorTypeLabel(key)}
              />
              <StatList
                title="By Risk Tier"
                data={data.vendors.byRiskTier}
                labelFormatter={(key) => vendorService.getRiskTierConfig(key).label}
              />
            </div>
          </motion.div>

          {/* Contract Breakdown */}
          <motion.div variants={ITEM_VARIANTS}>
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
              Contract Breakdown
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatList
                title="By Status"
                data={data.contracts.byStatus}
                labelFormatter={(key) => vendorService.getContractStatusConfig(key).label}
              />
              <StatList
                title="By Type"
                data={data.contracts.byType}
                labelFormatter={(key) => vendorService.getContractTypeLabel(key)}
              />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-2 gap-4 mt-4">
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-secondary-900 dark:text-white mb-2">Expiring Soon</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-secondary-600 dark:text-secondary-400">Expiring in 30 Days</span>
                      <span className="text-lg font-bold text-danger-600 dark:text-danger-400">
                        {formatNumber(data.contracts.expiringIn30Days)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-secondary-600 dark:text-secondary-400">Expiring in 90 Days</span>
                      <span className="text-lg font-bold text-warning-600 dark:text-warning-400">
                        {formatNumber(data.contracts.expiringIn90Days)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Additional Metrics */}
          <motion.div variants={ITEM_VARIANTS}>
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
              Additional Metrics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-secondary-900 dark:text-white mb-2">Overdue Obligations</h3>
                  <div className="text-3xl font-bold text-danger-600 dark:text-danger-400">
                    {formatNumber(data.obligations.overdueCount)}
                  </div>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                    Obligations past their due date
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-secondary-900 dark:text-white mb-2">Total Amendments</h3>
                  <div className="text-3xl font-bold text-secondary-900 dark:text-white">
                    {formatNumber(data.amendments.total)}
                  </div>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                    Contract amendments on record
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-secondary-900 dark:text-white mb-2">Documents</h3>
                  <div className="text-3xl font-bold text-secondary-900 dark:text-white">
                    {formatNumber(data.documents.total)}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-secondary-600 dark:text-secondary-400">Expiring in 30 Days</span>
                    <span className="text-sm font-semibold text-warning-600 dark:text-warning-400">
                      {formatNumber(data.documents.expiringIn30Days)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </>
      ) : null}
    </motion.div>
  );
}
